// **"You win!" / "You lose!"** (`player-chat-and-outcome` **D-5**, 2026-08-12).
//
// A match used to end without telling the player what happened. The terminal `Placement`
// (`waggle-proto` `MatchResult`) has always carried the answer — this reads it and says it. No
// server or protocol change: every field here is already on the wire.
//
// **The rule is: the lowest rank in the result is the win, and nothing else.** In a two-colony
// deathmatch that is exactly the duel result; at larger colony counts the lowest rank is still the
// only win, so the same sentence degrades correctly rather than needing a second one.
//
// **This used to read `rank === 1`** (Bugbot round 1, `pvp-ui-fixes`), which is the opposite of
// the wire: the server's one production constructor assigns ranks by `enumerate()` over the sorted
// placements — "`rank` 0 is the winner" (`waggle-server/src/match_thread.rs`) — and the codec
// carries the value verbatim. So the banner congratulated the loser and told the winner "You
// lose!", with every test green, because the fixtures were hand-written 1-based to match the
// component rather than the wire. Lowest-wins rather than `=== 0`, deliberately: it is the reading
// the Match screen's winner banner and the Summary already use (`sort by rank, take [0]`), so all
// three surfaces now agree by construction and none can be inverted alone by a rebase of the
// convention.
//
// **Deliberately live-mode only.** Rounds mode produces a *placement* among N players and a
// tournament mean across rounds, where "You lose" for fourth of six is a different and worse
// statement than losing a duel — and the graded review screen already communicates placement
// properly. The exclusion is structural rather than a predicate: `matchResult` is only ever set by
// the live wire binder (`ui/colony/bind-messages.ts`), and `AppRoutes` mounts this only in the live
// composition (see its own note there).
//
// **It is a layer over whatever screen is showing, not a screen's child.** The result lands while
// the player is on Colony or Match and `SessionController` immediately navigates to `/summary`, so
// a banner belonging to any one of those would flash for a frame and be gone. It therefore sits
// beside the router, survives that navigation, and pins itself to the TOP — clear of the replay
// transport and of the summary's own calls to action, neither of which it may cover. Dismissible,
// and dismissal is remembered for the session so it does not return on the next navigation.

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { useClientStore } from '../../../state/store';
import { launchConfetti, type ConfettiHandle } from '../../effects/confetti';

const LAYER_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  // Above the chat overlay (50), because a match that has ended outranks a conversation about it.
  zIndex: 60,
  display: 'flex',
  justifyContent: 'center',
  // The layer spans the width so the confetti has room to fall across it, but only the card itself
  // is a click target — everything under the banner stays usable, which is what "must not trap the
  // player" means in practice.
  pointerEvents: 'none',
  paddingTop: 24,
  // Tall enough for the burst to read, short enough to leave the screen's own middle alone.
  height: 260,
  overflow: 'hidden',
};

const CARD_STYLE: CSSProperties = {
  pointerEvents: 'auto',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3)',
  maxWidth: 520,
  margin: '0 16px',
  padding: '14px 14px 14px 20px',
  borderRadius: 'var(--r-lg)',
  background: 'var(--surface-app)',
  border: '1.5px solid var(--hud-line)',
  boxShadow: 'var(--shadow-sheet)',
};

const HEADLINE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 700,
  // Large, because this is the one sentence the whole match was for.
  fontSize: 34,
  lineHeight: 1.1,
  color: 'var(--ink-1)',
};

const SUBLINE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-sm)',
  color: 'var(--text-muted)',
  marginTop: 4,
};

const DISMISS_STYLE: CSSProperties = {
  flex: 'none',
  minWidth: 'var(--hit-target)',
  minHeight: 'var(--hit-target)',
  borderRadius: 'var(--r-pill)',
  background: 'var(--surface-card)',
  color: 'var(--ink-2)',
  border: '1.5px solid var(--line-strong)',
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-sm)',
  cursor: 'pointer',
};

export function MatchOutcomeBanner() {
  const matchResult = useClientStore((s) => s.matchResult);
  const ownColonyId = useClientStore((s) => s.colonyId);
  const [dismissed, setDismissed] = useState(false);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const burstRef = useRef<ConfettiHandle | null>(null);

  const placement =
    matchResult === null || ownColonyId === null
      ? undefined
      : matchResult.placements.find((p) => p.colonyId === ownColonyId);
  // A spectator, or a session whose colony is not in the result at all, is told nothing rather than
  // told it lost — the banner speaks only about the player's own colony.
  const won =
    placement !== undefined &&
    matchResult !== null &&
    matchResult.placements.every((p) => placement.rank <= p.rank);
  const visible = placement !== undefined && !dismissed;

  useEffect(() => {
    if (!visible || !won || layerRef.current === null) {
      return undefined;
    }
    // The burst belongs to the banner's own lifetime: dismissing unmounts this layer, which runs
    // the cleanup, which stops the ticker. There is no path that leaves one running.
    burstRef.current = launchConfetti(layerRef.current, { origin: { x: 0.5, y: 0.32 } });
    return () => {
      burstRef.current?.stop();
      burstRef.current = null;
    };
  }, [visible, won]);

  if (!visible) {
    return null;
  }

  return (
    <div ref={layerRef} data-testid="match-outcome-banner" data-outcome={won ? 'win' : 'loss'} style={LAYER_STYLE}>
      <div style={CARD_STYLE}>
        <span aria-hidden="true" style={{ fontSize: 34, lineHeight: 1 }}>
          {won ? '🏆' : '🥀'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* One assertive live region: the match ending is exactly the interruption `assertive`
              exists for, and it is announced once because this mounts once. */}
          <div role="status" aria-live="assertive" style={HEADLINE_STYLE}>
            {won ? 'You win!' : 'You lose!'}
          </div>
          <div style={SUBLINE_STYLE}>
            {won
              ? `Last colony standing — ${placement.livingCount} bees left.`
              : // `rank` is the wire's 0-based order; a person finishes "#2", never "#1" for
                // second place.
                `Finished #${placement.rank + 1} — ${placement.livingCount} bees left.`}
          </div>
        </div>
        <button data-testid="match-outcome-dismiss" aria-label="Dismiss" onClick={() => setDismissed(true)} style={DISMISS_STYLE}>
          ✕
        </button>
      </div>
    </div>
  );
}
